import { Head } from "@inertiajs/react";
import { useModalStack } from "@inertiaui/modal-react";
import { t } from "@travily/i18n";
import {
  creatorListingsPath,
  newAccountPayoutsPath,
  serviceCreatorPreviewPath,
  serviceCreatorStagePath,
} from "@travily/routes";
import type ListingsCreatorPublish from "@travily/types/pages/ListingsCreatorPublish";
import { Wizard } from "@ui/components/Wizard";
import { PublishStepView } from "@/components/PublishStepView/PublishStepView";
import { listingPriceLabel } from "@/lib/build-show-view-props";
import { useCreatorTopBarActions } from "@/lib/wizard/use-creator-top-bar-actions";
import { useWizardForm } from "@/lib/wizard/use-wizard-form";

const COLOR = "green" as const;

type FormData = {
  auto_publish_on_payouts_ready: boolean;
};

const ServicePublishStep = ({
  listing_id,
  listing_title,
  stage_index,
  stage_order,
  is_free,
  stripe_connected,
  stripe_connect_pending,
  auto_publish_on_payouts_ready,
  cover_image,
  price_cents,
  price_currency,
}: ListingsCreatorPublish) => {
  const { form, currentStage, totalStages, onBack, onPrimary, primaryPending } = useWizardForm<FormData>({
    routes: {
      stageUrl: (stage) => serviceCreatorStagePath(listing_id, stage),
      entryUrl: creatorListingsPath(),
    },
    stageName: "publish",
    stageIndex: stage_index,
    stageOrder: stage_order,
    initialData: { auto_publish_on_payouts_ready: auto_publish_on_payouts_ready ?? true },
  });
  const { visitModal } = useModalStack();

  const blockedOnPayouts = !is_free && !stripe_connected;

  const topBarActions = useCreatorTopBarActions({ saveHref: creatorListingsPath() });

  return (
    <>
      <Head title={t("web.services.creator.steps.publish.show.meta_title")}>
        <meta name="description" content={t("web.services.creator.steps.publish.show.meta_description")} />
      </Head>
      <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
        {topBarActions}
        <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
          {blockedOnPayouts
            ? t("web.services.creator.steps.publish.show.publish_cta_when_ready")
            : t("web.services.creator.steps.publish.show.publish_cta")}
        </Wizard.PrimaryButton>
        <Wizard.Stage
          heading={t("web.services.creator.steps.publish.show.title")}
          helper={t("web.services.creator.steps.publish.show.subheading")}
        >
          <PublishStepView
            kind="service"
            isFree={is_free}
            stripeConnected={stripe_connected}
            stripeConnectPending={stripe_connect_pending}
            autoPublishOnPayoutsReady={form.data.auto_publish_on_payouts_ready}
            onAutoPublishChange={(value) => form.setData("auto_publish_on_payouts_ready", value)}
            onSetUpPayouts={() => visitModal(newAccountPayoutsPath())}
            previewUrl={serviceCreatorPreviewPath(listing_id)}
            summary={{
              title: listing_title ?? "",
              priceLabel: listingPriceLabel("service", { free: is_free, price_cents, price_currency }),
              coverImage: cover_image,
            }}
          />
        </Wizard.Stage>
      </Wizard>
    </>
  );
};

export default ServicePublishStep;
